import type { ReactNode } from 'react';
import { FctIcono } from './marca';
import { FctBoton } from './acciones';
import { FctAviso } from './semantica';
import { fmtNumero } from '../formato';

/* ===========================================================================
 * Progreso de procesos, según la duración esperada:
 *   segundos     → FctProgresoLote    (banda en línea junto al botón que lo lanzó)
 *   1–2 minutos  → FctProgresoFases   (modal con fases y tiempo transcurrido)
 *   30–90 min    → FctProgresoLargo   (banda no bloqueante con métricas)
 * Colores: familia Proceso mientras corre; semáforo al terminar.
 * ========================================================================= */

export interface FctProgresoLoteProps {
  hechos: number;
  total: number;
  /** Qué se está procesando: «farmacias», «documentos»… */
  unidad: string;
  errores?: number;
}

export function FctProgresoLote({ hechos, total, unidad, errores = 0 }: FctProgresoLoteProps) {
  const pct = total > 0 ? Math.round((hechos / total) * 100) : 0;
  return (
    <div className="fct-progreso" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={hechos} aria-label={`Procesando ${unidad}`}>
      <div className="fct-progreso__pista">
        <i style={{ width: `${pct}%` }} />
      </div>
      <div className="fct-progreso__texto">
        <span>
          <span className="fct-mono">{fmtNumero(hechos)}</span> de <span className="fct-mono">{fmtNumero(total)}</span> {unidad}
        </span>
        {errores > 0 && <span style={{ color: 'var(--fct-riesgo-texto)' }}>{fmtNumero(errores)} con error</span>}
        <span className="fct-mono">{pct} %</span>
      </div>
    </div>
  );
}

export type FctEstadoFase = 'pendiente' | 'en-curso' | 'hecha' | 'fallida';

export interface FctProgresoFasesProps {
  abierto: boolean;
  titulo: string;
  fases: { nombre: string; estado: FctEstadoFase; detalle?: string }[];
  /** Texto de useFctTranscurrido. */
  transcurrido?: string;
  /** Solo se puede cerrar cuando termina o falla; no esconde un proceso en marcha. */
  onCerrar?: () => void;
  error?: string;
}

const ICONO_FASE = { pendiente: 'punto', 'en-curso': 'transmision', hecha: 'completado', fallida: 'alerta' } as const;

export function FctProgresoFases({ abierto, titulo, fases, transcurrido, onCerrar, error }: FctProgresoFasesProps) {
  if (!abierto) return null;
  const terminado = fases.every((f) => f.estado === 'hecha') || fases.some((f) => f.estado === 'fallida');
  return (
    <div className="fct-modal-fondo">
      <div className="fct-modal" role="dialog" aria-modal="true" aria-labelledby="fct-progreso-titulo">
        <div>
          <h2 id="fct-progreso-titulo" className="fct-titulo-tarjeta">{titulo}</h2>
          {transcurrido && <p className="fct-pie" style={{ margin: '4px 0 0' }}>Transcurrido <span className="fct-mono">{transcurrido}</span></p>}
        </div>
        <ol className="fct-fases">
          {fases.map((f) => (
            <li key={f.nombre} className={f.estado === 'hecha' ? 'fct-hecha' : f.estado === 'en-curso' ? 'fct-en-curso' : f.estado === 'fallida' ? 'fct-fallida' : undefined}>
              <FctIcono nombre={ICONO_FASE[f.estado]} />
              <span>
                {f.nombre}
                {f.detalle && <span className="fct-pie"> · {f.detalle}</span>}
              </span>
            </li>
          ))}
        </ol>
        {error && <FctAviso tipo="error" titulo="El proceso se ha detenido.">{error}</FctAviso>}
        <div className="fct-modal__pie">
          <FctBoton variante={terminado ? 'primario' : 'terciario'} disabled={!terminado} onClick={onCerrar}>
            {terminado ? 'Cerrar' : 'En curso…'}
          </FctBoton>
        </div>
      </div>
    </div>
  );
}

export interface FctProgresoLargoProps {
  titulo: string;
  estado: 'en-curso' | 'completado' | 'fallido';
  /** 0–100 si se conoce; si no, solo métricas. */
  porcentaje?: number;
  metricas?: { etiqueta: string; valor: string }[];
  transcurrido?: string;
  acciones?: ReactNode;
}

export function FctProgresoLargo({ titulo, estado, porcentaje, metricas = [], transcurrido, acciones }: FctProgresoLargoProps) {
  const tipo = estado === 'en-curso' ? 'progreso' : estado === 'completado' ? 'exito' : 'error';
  return (
    <div className={`fct-aviso fct-aviso--${tipo}`} role="status" style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <FctIcono nombre={estado === 'en-curso' ? 'transmision' : estado === 'completado' ? 'completado' : 'alerta'} />
        <b>{titulo}</b>
        {transcurrido && <span className="fct-mono" style={{ fontSize: 12 }}>{transcurrido}</span>}
        {acciones && <span style={{ marginLeft: 'auto' }}>{acciones}</span>}
      </div>
      {porcentaje !== undefined && (
        <div className="fct-progreso__pista" aria-hidden="true">
          <i style={{ width: `${Math.max(0, Math.min(100, porcentaje))}%` }} />
        </div>
      )}
      {metricas.length > 0 && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12.5 }}>
          {metricas.map((m) => (
            <span key={m.etiqueta}>
              {m.etiqueta} <b className="fct-mono">{m.valor}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
